import {ActionIcon, Badge, Button, Card, Group, SimpleGrid, Stack, Text} from '@mantine/core'
import {IconCheck, IconMapPin, IconPlus, IconX} from '@tabler/icons-react'
import {useState} from 'react'

import type {SavedLocation} from 'src/customers/saved-locations/schemas'
import {useCustomerSavedLocations} from 'src/customers/saved-locations/use-saved-locations'
import {LocationEditModal} from 'src/jobs/location-edit-modal'
import type {JobLocation} from 'src/jobs/types'
import {outOfStateLabel} from 'src/lib/texas-bounds'
import {OutOfStateBadge} from 'src/ui/out-of-state-badge'

import {locationFromSavedSite, savedSiteOf} from './saved-sites'

type SiteCardsProps = {
    customerId: string | null
    locations: JobLocation[]
    error?: string
    onChange: (locations: JobLocation[]) => void
}

export function SiteCards({customerId, locations, error, onChange}: SiteCardsProps) {
    const {data: savedSites = [], isLoading} = useCustomerSavedLocations(
        customerId ?? '',
        undefined,
        {
            enabled: Boolean(customerId),
        },
    )
    const [adding, setAdding] = useState(false)

    const savedIdOf = new Map(
        locations.map((location) => [location, savedSiteOf(location, savedSites)?.id ?? null]),
    )
    const pickedSavedIds = new Set([...savedIdOf.values()].filter(Boolean))
    const otherSites = locations.filter((location) => savedIdOf.get(location) == null)

    function toggleSaved(site: SavedLocation) {
        onChange(
            pickedSavedIds.has(site.id)
                ? locations.filter((location) => savedIdOf.get(location) !== site.id)
                : [...locations, locationFromSavedSite(site)],
        )
    }

    if (!customerId) {
        return (
            <Text size="sm" c="dimmed">
                Select a customer to see sites.
            </Text>
        )
    }

    return (
        <Stack gap="xs">
            <Group justify="space-between">
                <Text size="sm" fw={500}>
                    Site{' '}
                    <Text span c="red">
                        *
                    </Text>
                </Text>
                <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => setAdding(true)}
                >
                    New site
                </Button>
            </Group>
            {!isLoading && savedSites.length === 0 && otherSites.length === 0 ? (
                <Text size="sm" c="dimmed">
                    No saved sites for this customer
                </Text>
            ) : null}
            <SimpleGrid cols={{base: 1, sm: 2, md: 3}} spacing="xs">
                {savedSites.map((site) => {
                    const picked = pickedSavedIds.has(site.id)
                    return (
                        <Card
                            key={site.id}
                            component="button"
                            type="button"
                            withBorder
                            padding="sm"
                            aria-pressed={picked}
                            onClick={() => toggleSaved(site)}
                            style={{
                                textAlign: 'left',
                                cursor: 'pointer',
                                borderColor: picked
                                    ? 'var(--mantine-primary-color-filled)'
                                    : undefined,
                                borderWidth: picked ? 2 : undefined,
                            }}
                        >
                            <Group gap="xs" wrap="nowrap" align="flex-start">
                                {picked ? (
                                    <IconCheck
                                        size={16}
                                        color="var(--mantine-primary-color-filled)"
                                    />
                                ) : (
                                    <IconMapPin size={16} />
                                )}
                                <Stack gap={0} style={{minWidth: 0}}>
                                    <Text size="sm" fw={500} truncate>
                                        {site.name}
                                    </Text>
                                    {site.fullAddress ? (
                                        <Text size="xs" c="dimmed" truncate>
                                            {site.fullAddress}
                                        </Text>
                                    ) : null}
                                    <OutOfStateBadge
                                        mt={4}
                                        label={outOfStateLabel({
                                            latitude: site.latitude,
                                            longitude: site.longitude,
                                            addressText: site.fullAddress,
                                        })}
                                    />
                                </Stack>
                            </Group>
                        </Card>
                    )
                })}
                {otherSites.map((site) => (
                    <Card key={`new-${site.name}-${site.latitude}`} withBorder padding="sm">
                        <Group justify="space-between" wrap="nowrap" align="flex-start">
                            <Stack gap={0} style={{minWidth: 0}}>
                                <Group gap={4} wrap="nowrap">
                                    <IconCheck
                                        size={16}
                                        color="var(--mantine-primary-color-filled)"
                                    />
                                    <Text size="sm" fw={500} truncate>
                                        {site.name}
                                    </Text>
                                </Group>
                                <Badge size="xs" variant="light" mt={4}>
                                    {site.rememberAsSavedLocation
                                        ? 'New, will be saved'
                                        : 'This job only'}
                                </Badge>
                                <OutOfStateBadge mt={4} label={outOfStateLabel(site)} />
                            </Stack>
                            <ActionIcon
                                variant="subtle"
                                aria-label={`Remove ${site.name}`}
                                onClick={() =>
                                    onChange(locations.filter((location) => location !== site))
                                }
                            >
                                <IconX size={14} />
                            </ActionIcon>
                        </Group>
                    </Card>
                ))}
            </SimpleGrid>
            {error ? (
                <Text size="sm" c="red">
                    {error}
                </Text>
            ) : null}
            <LocationEditModal
                source="job_creation_editor"
                isOpen={adding}
                onClose={() => setAdding(false)}
                onSave={(location) => onChange([...locations, location])}
                mode="create-new"
                customerId={customerId}
                title="New site"
                nameLabel="Site name"
                enableProviderFallback
            />
        </Stack>
    )
}
